import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { EmployeeFormModal } from '../components/EmployeeFormModal';
import { SalaryImportModal } from '../components/SalaryImportModal';
import { SearchableDropdown } from '../components/SearchableDropdown';
import { TableActionMenu } from '../components/TableActionMenu';
import { useAuth } from '../context/AuthContext';
import { employeesApi, type EmployeeListItem } from '../lib/api/employees';
import { salaryImportApi, type ImportKind } from '../lib/api/salary-import';
import { exportSheet } from '../lib/export-generic';
import { formatVND } from '../lib/format';
import { useToast } from '../lib/use-toast';

const CONTRACT_LABEL: Record<string, string> = {
  CT: 'Chính thức',
  TV: 'Thử việc',
  DV: 'Freelancer',
};

const CONTRACT_TONE: Record<string, string> = {
  CT: 'blue',
  TV: 'yellow',
  DV: 'purple',
};

function shortDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function shortDateNoYear(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function initials(name: string): string {
  return name.split(/\s+/).slice(-2).map((w) => w[0]).join('').toUpperCase();
}

function avatarColor(name: string): string {
  const palette = ['#2563EB', '#7c3aed', '#dc2626', '#16a34a', '#f59e0b', '#0d9488', '#8b5cf6', '#0891b2', '#ea580c', '#059669', '#9333ea'];
  const h = name.split('').reduce((s, c) => s + c.charCodeAt(0), 0);
  return palette[h % palette.length];
}

const PAGE_SIZE = 10;
const EMPLOYMENT_FILTER_OPTIONS = [
  { value: 'all', label: 'Tất cả hợp đồng' },
  { value: 'CT', label: 'Chính thức' },
  { value: 'TV', label: 'Thử việc' },
  { value: 'DV', label: 'Freelancer' },
];

export function HrPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast, showToast } = useToast();
  const [importKind, setImportKind] = useState<ImportKind | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [items, setItems] = useState<EmployeeListItem[]>([]);
  const [totalActive, setTotalActive] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [employmentFilter, setEmploymentFilter] = useState<'all' | 'CT' | 'TV' | 'DV'>('all');
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const canWrite = user?.role === 'KTT' || user?.role === 'NV_KT';
  const linkedEmployeeId = searchParams.get('employeeId');

  useEffect(() => {
    if (!linkedEmployeeId || !canWrite) return;
    setEditingId(linkedEmployeeId);
    setFormOpen(true);
    setSearchParams({}, { replace: true });
  }, [canWrite, linkedEmployeeId, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await employeesApi.list(search.trim() || undefined);
      setItems(r.items);
      setTotalActive(r.syncMeta?.totalActive ?? r.paging?.total ?? r.items.length);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, [search]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search]);

  const activeCount = items.filter((e) => e.active).length;
  const probationCount = items.filter((e) => e.employmentType === 'TV').length;
  const freelanceCount = items.filter((e) => e.employmentType === 'DV').length;

  const filteredItems = useMemo(() => employmentFilter === 'all' ? items : items.filter((item) => item.employmentType === employmentFilter), [items, employmentFilter]);
  const pageCount = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const pagedItems = useMemo(
    () => filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredItems, page],
  );
  const showFrom = filteredItems.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const showTo = Math.min(page * PAGE_SIZE, filteredItems.length);

  const rowClick = (id: string) => {
    if (!canWrite) return;
    setEditingId(id); setFormOpen(true);
  };

  return (
    <AppShell title="Quản lý lương nhân viên" subtitle="Danh sách NV · Cấu hình lương · NPT · TK ngân hàng">
      {err && <div className="pay-table-error">Lỗi: {err}</div>}

      {/* KPI cards — match prototype: stripe + per-tone colored label & value */}
      <div className="hr-kpi-grid">
        <div className="hr-kpi hr-kpi-blue">
          <div className="hr-kpi-stripe" />
          <div className="hr-kpi-body">
            <div className="hr-kpi-label">Tổng NV</div>
            <div className="hr-kpi-value">{totalActive}<span>người</span></div>
          </div>
        </div>
        <div className="hr-kpi hr-kpi-green">
          <div className="hr-kpi-stripe" />
          <div className="hr-kpi-body">
            <div className="hr-kpi-label">Đang làm việc</div>
            <div className="hr-kpi-value">{activeCount}</div>
          </div>
        </div>
        <div className="hr-kpi hr-kpi-yellow">
          <div className="hr-kpi-stripe" />
          <div className="hr-kpi-body">
            <div className="hr-kpi-label">Thử việc</div>
            <div className="hr-kpi-value">{probationCount}</div>
          </div>
        </div>
        <div className="hr-kpi hr-kpi-purple">
          <div className="hr-kpi-stripe" />
          <div className="hr-kpi-body">
            <div className="hr-kpi-label">Freelancer</div>
            <div className="hr-kpi-value">{freelanceCount}</div>
          </div>
        </div>
      </div>

      {/* Main card */}
      <div className="pay-page-card">
        <div className="pay-toolbar">
          <div className="pay-toolbar-left">
            <div className="pay-search hr-search">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
              <input placeholder="Tìm mã NV, họ tên..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <SearchableDropdown
              className="hr-contract-filter"
              ariaLabel="Lọc loại hợp đồng"
              value={employmentFilter}
              options={EMPLOYMENT_FILTER_OPTIONS}
              searchPlaceholder="Tìm loại hợp đồng..."
              onChange={(value) => { setEmploymentFilter(value as typeof employmentFilter); setPage(1); }}
            />
          </div>
          <div className="pay-toolbar-right">
            <button className="pay-btn-outline" type="button" onClick={() => { exportSheet('nhan-vien', filteredItems, [{ header: 'Mã NV', key: 'code' }, { header: 'Họ tên', key: 'name' }, { header: 'Chức vụ', key: 'position' }, { header: 'Phòng ban', key: 'departmentName' }, { header: 'Loại HĐ', key: 'employmentType' }, { header: 'Tổng thu nhập', key: 'totalIncome' }, { header: 'Số NPT', key: 'dependentsCount' }], 'Nhân viên'); showToast(`Đã xuất ${filteredItems.length} nhân viên`); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></svg>
              Xuất Excel
            </button>
              <button className="hr-import-btn" type="button" onClick={() => setImportKind('salary-config')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" x2="12" y1="3" y2="15" /></svg>
                Import Excel
              </button>
            {canWrite && (
              <button
                className="pay-btn-dark"
                type="button"
                onClick={() => { setEditingId(null); setFormOpen(true); }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M5 12h14M12 5v14" /></svg>
                Thêm NV mới
              </button>
            )}
          </div>
        </div>

        <div className="pay-table-wrap hr-table-wrap">
          {loading ? <div className="pay-empty">Đang tải…</div> : filteredItems.length === 0 ? (
            <div className="pay-empty">
              <div className="pay-empty-icon">👤</div>
              Không có NV khớp bộ lọc.
            </div>
          ) : (
            <table className="pay-tbl hr-tbl">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>Mã NV</th>
                  <th style={{ minWidth: 220 }}>Họ tên · Chức vụ</th>
                  <th style={{ width: 110 }}>Phòng ban</th>
                  <th style={{ width: 100 }}>Ngày vào</th>
                  <th style={{ width: 130 }}>Loại HĐ</th>
                  <th style={{ width: 140, textAlign: 'right' }}>Tổng thu nhập</th>
                  <th style={{ width: 60, textAlign: 'center' }}>NPT</th>
                  <th style={{ width: 130 }}>Trạng thái</th>
                  {canWrite && <th style={{ width: 100, textAlign: 'right' }}>Thao tác</th>}
                </tr>
              </thead>
              <tbody>
                {pagedItems.map((e) => {
                  const isProbation = e.employmentType === 'TV';
                  const isFreelance = e.employmentType === 'DV';
                  const probationEndStr = isProbation && e.probationEnd ? shortDateNoYear(e.probationEnd) : null;
                  const probationSalary = isProbation && e.totalIncome != null && e.probationSalaryPct
                    ? Math.round(e.totalIncome * e.probationSalaryPct / 100)
                    : null;
                  // Highlight NV vừa lên CT trong 30 ngày qua
                  const justPromoted = e.employmentType === 'CT' && e.probationEnd
                    ? (() => {
                        const now = new Date();
                        const end = new Date(e.probationEnd);
                        const days = (now.getTime() - end.getTime()) / 86_400_000;
                        return days >= 0 && days <= 30;
                      })()
                    : false;
                  const promotedDateStr = justPromoted && e.probationEnd ? shortDateNoYear(e.probationEnd) : null;
                  const tone = CONTRACT_TONE[e.employmentType ?? ''] ?? 'gray';

                  return (
                    <tr key={e.id} className={`hr-row ${justPromoted ? 'hr-row-highlight' : ''}`} onClick={() => rowClick(e.id)} style={{ cursor: canWrite ? 'pointer' : 'default' }}>
                      <td><span className="hr-code-pill">{e.code}</span></td>
                      <td>
                        <div className="hr-name-cell">
                          <div className="hr-avatar" style={{ background: avatarColor(e.name) }}>{initials(e.name)}</div>
                          <div className="hr-name-meta">
                            <b>{e.name}</b>
                            <span>{e.position ?? '—'}</span>
                          </div>
                        </div>
                      </td>
                      <td className="hr-td-dept">{e.departmentName ?? '—'}</td>
                      <td className="hr-td-date">{shortDate(e.hireDate)}</td>
                      <td>
                        <div className="hr-contract-cell">
                          <span className={`hr-pill hr-pill-tone-${tone}`}>{e.employmentType ? CONTRACT_LABEL[e.employmentType] ?? e.employmentType : '—'}</span>
                          {probationEndStr && <span className="hr-contract-sub muted">TV kết thúc {probationEndStr}</span>}
                          {promotedDateStr && <span className="hr-contract-sub green">Vừa lên CT {promotedDateStr}</span>}
                        </div>
                      </td>
                      <td className="hr-td-money">
                        <div>
                          <div className="hr-money">{e.totalIncome != null ? formatVND(e.totalIncome) : '—'}</div>
                          {probationSalary != null && <div className="hr-money-sub">TV {e.probationSalaryPct}%: {formatVND(probationSalary)}</div>}
                          {isFreelance && <div className="hr-money-sub purple">Trả theo dự án</div>}
                        </div>
                      </td>
                      <td className="hr-td-npt">{e.dependentsCount}</td>
                      <td>
                        {isProbation ? (
                          <span className="hr-status-dot yellow">
                            <span className="dot" /> Thử việc
                          </span>
                        ) : (
                          <span className={`hr-status-dot ${e.active ? 'green' : 'gray'}`}>
                            <span className="dot" /> {e.active ? 'Đang làm việc' : 'Đã nghỉ'}
                          </span>
                        )}
                      </td>
                      {canWrite && (
                        <td className="hr-td-action" onClick={(ev) => ev.stopPropagation()}>
                          <TableActionMenu
                            open={actionMenuId === e.id}
                            onToggle={() => setActionMenuId((current) => current === e.id ? null : e.id)}
                            onClose={() => setActionMenuId(null)}
                            ariaLabel={`Thao tác ${e.name}`}
                            items={[{ label: 'Sửa cấu hình lương', onClick: () => rowClick(e.id) }]}
                          />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {filteredItems.length > 0 && (
          <div className="hr-pagination">
            <div className="hr-page-info">Hiển thị {showFrom}-{showTo} của {filteredItems.length} nhân viên</div>
            <div className="hr-page-nav">
              <button
                type="button"
                className="hr-page-btn hr-page-nav-btn"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >◀ Trước</button>
              {pageNumbers(page, pageCount).map((p, i) =>
                p === '…' ? (
                  <span key={`ellipsis-${i}`} className="hr-page-ellipsis">…</span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    className={`hr-page-btn ${p === page ? 'active' : ''}`}
                    onClick={() => setPage(p as number)}
                  >{p}</button>
                ),
              )}
              <button
                type="button"
                className="hr-page-btn hr-page-nav-btn"
                disabled={page === pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              >Sau ▶</button>
            </div>
          </div>
        )}
      </div>

      <EmployeeFormModal
        open={formOpen}
        editingId={editingId}
        onClose={() => { setFormOpen(false); setEditingId(null); }}
        onSaved={() => { setFormOpen(false); setEditingId(null); showToast(editingId ? 'Đã cập nhật NV' : 'Đã thêm NV mới'); load(); }}
      />

      <SalaryImportModal
        open={importKind !== null}
        kind={importKind}
        templateName="Template cấu hình lương NV.xlsx"
        onDownloadTemplate={() => salaryImportApi.downloadTemplate('salary-config')}
        onClose={() => setImportKind(null)}
        onDone={() => { setImportKind(null); showToast('Đã import xong'); load(); }}
      />

      {toast && <div className="coa-toast">{toast}</div>}
    </AppShell>
  );
}

function pageNumbers(current: number, total: number): Array<number | '…'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: Array<number | '…'> = [1];
  if (current > 3) pages.push('…');
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (current < total - 2) pages.push('…');
  pages.push(total);
  return pages;
}
